import { z } from 'zod';
/**
 * Sổ kiến thức tổ chức — APM-H-i (`docs/knowledge/README.md` là hạt giống).
 *
 * Mỗi mục nói nó **sống ở đâu**: `prompt` (phán đoán, vào system prompt của
 * skill cùng tag), `code` (đủ chắc để thành luật trong solver — sổ chỉ ghi lại,
 * không thay code), `metric` (cần đo trên tổ chức này; có `metric` thì khối
 * kiến thức kèm con số thật).
 */
/**
 * `critic` — APM-CRIT-e: mục **chỉ người phản biện đọc** (checklist, luật miền
 * của tổ chức để đối chiếu kết quả), không vào prompt của model lập kết quả.
 * Skill khai `critique.knowledgeTags: ['critic']` mới đọc; "đang vào skill" ở
 * trang Kiến thức tính cả đường này.
 *
 * `harness:invariant` — APM-H-ar: bài học **bất biến** của harness ("đọc dữ
 * liệu trước khi nộp", "trích biên nhận cho mọi con số"), vào **lớp 1** của
 * **mọi** skill mà không cần skill khai tag, trần riêng
 * `INVARIANT_KNOWLEDGE_BUDGET_TOKENS` (300). Vẫn là `org_knowledge`: vẫn qua
 * duyệt, vẫn là DỮ LIỆU tham khảo trong thẻ `du_lieu_*`, không phải luật mới.
 */
export const KnowledgeTag = z.enum([
    'estimate',
    'assign',
    'plan',
    'risk',
    'retro',
    'okr',
    'critic',
    'harness:invariant',
]);
/** Tag đưa mục vào lớp 1 của mọi skill — APM-H-ar. */
export const INVARIANT_KNOWLEDGE_TAG = 'harness:invariant';
export const KnowledgeLives = z.enum(['prompt', 'code', 'metric']);
/** `ai_suggested`: bài học AI rút ra (what_if, retro) — người duyệt mới thành `active`. */
export const KnowledgeStatus = z.enum(['active', 'disabled', 'ai_suggested']);
/** Chỉ số đo được trên tổ chức mà một mục có thể kèm. Thêm khoá = thêm hàm đo ở `knowledge-metrics.ts`. */
export const KnowledgeMetric = z.enum(['overdue_task_pct', 'late_start_pct']);
export const OrgKnowledgeView = z.object({
    id: z.uuid(),
    title: z.string(),
    body: z.string(),
    tags: z.array(KnowledgeTag),
    lives: z.array(KnowledgeLives),
    source: z.string().nullable(),
    metric: KnowledgeMetric.nullable(),
    status: KnowledgeStatus,
    /** `YYYY-MM` — từ khi mục này được ghi nhận. */
    since: z.string().nullable(),
    createdBy: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    /** Skill đang đọc mục này (giao tag với `skill.knowledgeTags`) — chỉ khi `active`. */
    usedBySkills: z.array(z.string()),
});
export const CreateKnowledgeInput = z.object({
    title: z.string().trim().min(3).max(200),
    body: z.string().trim().min(10).max(4_000),
    tags: z.array(KnowledgeTag).min(1).max(5),
    lives: z.array(KnowledgeLives).min(1).max(3),
    source: z.string().trim().max(300).nullable().optional(),
    metric: KnowledgeMetric.nullable().optional(),
    since: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Dạng YYYY-MM')
        .nullable()
        .optional(),
    /** Bài học AI đề xuất ghi vào ở `ai_suggested`; người thêm tay mặc định `active`. */
    status: KnowledgeStatus.optional(),
});
export const UpdateKnowledgeInput = CreateKnowledgeInput.omit({ status: true }).partial();
export const SetKnowledgeStatusInput = z.object({ status: KnowledgeStatus });
/**
 * Tác động của việc đổi một mục: skill nào đọc tag ấy và bao nhiêu lượt gọi 24h
 * qua sẽ mất cache — như `PromptActivationImpact` của APM-H-f. UI hiện **trước** khi lưu.
 */
export const KnowledgeImpact = z.object({
    skills: z.array(z.object({
        skill: z.string(),
        tags: z.array(KnowledgeTag),
        callsLast24h: z.number().int().nonnegative(),
        runsLast24h: z.number().int().nonnegative(),
        /**
         * Token khối kiến thức skill này sẽ nhận, và trần của nó — APM-H-ag.
         *
         * Có mặt vì trần là thứ **im lặng**: vượt thì mục bị bỏ và người vừa thêm
         * nó không biết. Hiện con số ngay lúc sắp lưu là cách rẻ nhất để không ai
         * phải đi tìm câu trả lời trong dấu vết.
         */
        tokens: z.number().int().nonnegative().default(0),
        budgetTokens: z.number().int().positive().default(3_000),
    })),
});
//# sourceMappingURL=org-knowledge.js.map